import { useCallback } from "react";
import { apiRequest } from "./api";
import useApiQuery from "./useApiQuery";

const STATUS_FROM_API = { Pending: "requested", Paid: "paid", Rejected: "rejected" };
const STATUS_TO_API = { requested: "Pending", paid: "Paid", rejected: "Rejected" };

export function fromApi(withdrawal) {
  return {
    ...withdrawal,
    status: STATUS_FROM_API[withdrawal.status],
    requestedAt: new Date(withdrawal.requestedAt),
    processedAt: withdrawal.processedAt ? new Date(withdrawal.processedAt) : null,
  };
}

// One page of USDC withdrawal requests from `basePath`/withdrawals plus the totals at
// `basePath`, for both queues: referral rewards (/api/admin/referrals) and trader payouts
// (/api/admin/payouts). `markPaid` and `reject` resolve to { withdrawal } or { error },
// so the modals can show the message inline.
export default function useWithdrawalRequests(basePath, { page, pageSize, status, query, filters }) {
  const totals = useApiQuery(basePath);
  const list = useApiQuery(`${basePath}/withdrawals`, {
    ...filters,
    page,
    pageSize,
    status: status ? STATUS_TO_API[status] : null,
    q: query.trim(),
  });

  const { reload: reloadTotals } = totals;
  const { reload: reloadList } = list;

  const resolve = useCallback(
    async (id, action, body) => {
      try {
        const { withdrawal } = await apiRequest(`${basePath}/withdrawals/${id}/${action}`, {
          method: "POST",
          body,
        });
        // The request may now fall outside the status filter, and a rejection returns
        // the amount to the trader's balance, so both lists move
        reloadList();
        reloadTotals();
        return { withdrawal: fromApi(withdrawal) };
      } catch (err) {
        // Someone else got to it first: pull the current state
        if (err.status === 409) reloadList();
        return { error: err.message };
      }
    },
    [basePath, reloadList, reloadTotals]
  );

  const markPaid = useCallback((id, txHash) => resolve(id, "paid", { txHash }), [resolve]);
  const reject = useCallback((id, reason) => resolve(id, "reject", { reason }), [resolve]);

  return {
    requests: list.data ? list.data.withdrawals.map(fromApi) : [],
    total: list.data?.total ?? 0,
    stats: totals.data?.stats ?? null,
    status: list.data ? (list.status === "error" ? "error" : "ready") : list.status,
    loading: list.status === "loading",
    reload: reloadList,
    markPaid,
    reject,
  };
}
